import React, {
    createContext, useCallback, useContext, useMemo, useState
} from 'react';
import {
    DE_STUDIO_OPCODE_SIMPLE_DEFAULT
} from '../config/featureFlags';

const STORAGE_KEY = 'ds:opcode-editor-mode';

const OpcodeEditorModeContext = createContext(null);

function readStoredMode() {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        if (v === 'advanced' || v === 'simple') return v;
    } catch { /* ignore */ }
    return DE_STUDIO_OPCODE_SIMPLE_DEFAULT ? 'simple' : 'advanced';
}

export function OpcodeEditorModeProvider({
    children
}) {
    const [mode, setModeState] = useState(readStoredMode);

    const setMode = useCallback((next) => {
        setModeState(next);
        try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    }, []);

    const toggleMode = useCallback(() => {
        setMode(mode === 'simple' ? 'advanced' : 'simple');
    }, [mode, setMode]);

    const value = useMemo(() => ({
        mode,
        isSimple: mode === 'simple',
        isAdvanced: mode === 'advanced',
        setMode,
        toggleMode,
    }), [mode, setMode, toggleMode]);

    return (
        <OpcodeEditorModeContext.Provider value={value}>
            {children}
        </OpcodeEditorModeContext.Provider>
    );
}

export function useOpcodeEditorMode() {
    const ctx = useContext(OpcodeEditorModeContext);
    if (!ctx) {
        throw new Error('useOpcodeEditorMode must be used within OpcodeEditorModeProvider');
    }
    return ctx;
}

/** Safe fallback when provider is optional (step editors). */
export function useOpcodeEditorModeOptional() {
    return useContext(OpcodeEditorModeContext);
}
